import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Minus, Plus, X, ShieldCheck, CheckCircle2, Copy, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { trackLead } from "@/lib/metaPixel";

const field =
  "w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 transition-colors";

export const ReservationModal = () => {
  const { reserveOpen, setReserveOpen, items, updateQty, removeItem, subtotal, keyOf, clear } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — must stay empty

  useEffect(() => {
    if (reserveOpen && user) {
      setForm((f) => ({ full_name: f.full_name || user.name || "", email: f.email || user.email || "" }));
    }
  }, [reserveOpen, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleOpenChange = (o) => {
    setReserveOpen(o);
    if (!o) setTimeout(() => setDone(null), 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const primary = items[0];
      const { data } = await api.post("/vip", {
        full_name: form.full_name,
        email: form.email,
        product_id: primary.product_id,
        product_name: primary.product_name,
        color: primary.color,
        shape: primary.shape,
        lens: primary.lens,
        items: items.map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name,
          color: it.color,
          shape: it.shape,
          lens: it.lens,
        })),
        marketing_consent: consent,
        website,
      });
      setDone(data);
      // Fire Meta Lead once after a successful VIP signup (deduped by email).
      trackLead((data?.email || form.email || "").toLowerCase());
      clear();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not complete your VIP signup");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={reserveOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="reservation-modal"
        className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col"
      >
        {done ? (
          <div className="px-8 pt-8 pb-7 text-center" data-testid="vip-success">
            <div className="w-14 h-14 rounded-full bg-zinc-900 text-white flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tighter text-zinc-900">
              You're on the VIP list
            </h2>
            <p className="mt-2 text-sm text-zinc-500">
              Thanks, {form.full_name?.split(" ")[0] || "friend"} — we've saved your spot for the{" "}
              <span className="font-semibold text-zinc-900" data-testid="vip-product">{done.product_name || "Vsense glasses"}</span>.
            </p>

            <div className="mt-6 rounded-2xl border border-zinc-200 bg-gradient-to-b from-zinc-50 to-white p-5 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-700">
                What happens next
              </p>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                As a VIP member you'll receive a <span className="font-semibold text-zinc-900">private preorder link</span> and your{" "}
                <span className="font-semibold text-zinc-900">40%-off launch offer</span> before public preorders open.
              </p>
              <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-zinc-400">
                <Lock className="w-3.5 h-3.5 shrink-0 mt-px" />
                Joining VIP Early Access does not complete a purchase and does not guarantee inventory. No payment was collected.
              </p>
            </div>

            <button
              data-testid="vip-close"
              onClick={() => handleOpenChange(false)}
              className="mt-6 w-full bg-zinc-900 text-white text-sm font-medium px-7 py-3 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
            >
              Done
            </button>
            <p className="mt-3 text-xs text-zinc-400">Free to join · no payment · no credit card</p>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100 text-left">
              <DialogTitle className="font-display text-xl font-extrabold tracking-tighter">
                Join Vsense VIP Early Access
              </DialogTitle>
              <DialogDescription className="flex items-center gap-1.5 text-emerald-700">
                <ShieldCheck className="w-4 h-4" /> Free · no payment · no credit card
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <p className="text-sm text-zinc-500 py-6 text-center">Choose your preferred pair to join.</p>
              ) : (
                <div className="space-y-4">
                  {items.map((it) => {
                    const k = keyOf(it);
                    return (
                      <div key={k} data-testid={`modal-item-${it.product_id}`} className="flex gap-3">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F5F5F7] shrink-0">
                          <img src={it.image} alt={it.product_name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-zinc-900">{it.product_name}</p>
                            <button onClick={() => removeItem(k)} className="text-zinc-400 hover:text-zinc-900 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-zinc-500">{[it.color, it.shape, it.lens].filter(Boolean).join(" · ")}</p>
                          <div className="mt-2 flex items-center border border-zinc-200 rounded-full w-fit">
                            <button onClick={() => updateQty(k, it.quantity - 1)} className="p-1.5 text-zinc-600 hover:text-zinc-900">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="px-2 text-sm w-6 text-center">{it.quantity}</span>
                            <button onClick={() => updateQty(k, it.quantity + 1)} className="p-1.5 text-zinc-600 hover:text-zinc-900">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="rounded-xl border border-zinc-200 bg-gradient-to-b from-zinc-50 to-white px-4 py-3">
                    <p className="text-sm font-semibold text-zinc-900">VIP members save 40% at launch</p>
                    <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-relaxed text-zinc-500">
                      <Lock className="w-3.5 h-3.5 shrink-0 mt-px" />
                      You'll get a private preorder link and your 40%-off launch offer before public preorders open. Joining doesn't complete a purchase or guarantee inventory — no payment is collected.
                    </p>
                    <p data-testid="vip-selection-note" className="mt-2 text-[11px] leading-relaxed text-zinc-500">
                      Your selection helps us plan inventory. You'll be able to choose from all available colors and options when VIP preorders open.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="add-another-pair"
                    onClick={() => setReserveOpen(false)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-full border border-dashed border-zinc-300 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-900 hover:text-zinc-900 active:scale-[0.99] transition-all duration-200"
                  >
                    <Plus className="w-4 h-4" /> Add another pair
                  </button>
                </div>
              )}
            </div>

            <form onSubmit={submit} className="border-t border-zinc-100 px-6 py-5 space-y-3">
              <input required data-testid="vip-name" className={field} placeholder="Full name" value={form.full_name} onChange={set("full_name")} />
              <input required type="email" data-testid="vip-email" className={field} placeholder="Email" value={form.email} onChange={set("email")} />
              {/* Honeypot field — hidden from users, catches bots */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="vip-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
                <input
                  type="checkbox"
                  data-testid="vip-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                />
                <span className="text-xs text-zinc-500 leading-relaxed">
                  Keep me updated about my VIP launch offer and Vsense news. (Optional)
                </span>
              </label>
              <button
                type="submit"
                disabled={submitting || items.length === 0}
                data-testid="vip-submit"
                className="w-full bg-zinc-900 text-white text-sm font-semibold py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.99] transition-all duration-200 disabled:opacity-60"
              >
                {submitting ? "Joining…" : "Join VIP Early Access"}
              </button>
              <p className="text-center text-xs text-zinc-400">
                No payment collected. Joining does not complete a purchase or guarantee inventory.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
